import { useCallback, useState } from 'react';

const SIZE_ORDER = ['1x1', '2x1', '1x2'];

const createSelectionKey = (widgetId, size) => `${widgetId}:${size}`;

const parseSelectionKey = (selectionKey) => {
  const separatorIndex = selectionKey.indexOf(':');
  if (separatorIndex < 0) {
    return { widgetId: null, size: null };
  }

  return {
    widgetId: selectionKey.slice(0, separatorIndex),
    size: selectionKey.slice(separatorIndex + 1),
  };
};

const parseSizeToDimensions = (size) => {
  const [width, height] = String(size)
    .split('x')
    .map((value) => Number(value));

  if (!Number.isFinite(width) || !Number.isFinite(height)) {
    return null;
  }

  return { width, height };
};

export const getAvailableSizes = (widget) => {
  const sizes = widget?.sizes || {};
  const orderedSizes = SIZE_ORDER.filter((size) => Boolean(sizes[size]));
  const customSizes = Object.keys(sizes).filter(
    (size) => Boolean(sizes[size]) && !SIZE_ORDER.includes(size),
  );

  return [...orderedSizes, ...customSizes];
};

const createWidgetLayoutItem = (widgetId, size, widgetLookup) => {
  const widget = widgetLookup?.get(String(widgetId));
  if (!widget) {
    return null;
  }

  const parsedDimensions = parseSizeToDimensions(size);
  if (!parsedDimensions) {
    return null;
  }

  return {
    display: true,
    w: parsedDimensions.width,
    h: parsedDimensions.height,
    isDraggable: true,
    locked: false,
    x: 0,
    y: 10000,
    id: widget.id,
  };
};

const useWidgetSelection = () => {
  const [selectedStyleKeys, setSelectedStyleKeys] = useState(() => new Set());
  const [expandedWidgetId, setExpandedWidgetId] = useState(null);

  const isStyleSelected = useCallback(
    (widgetId, size) => selectedStyleKeys.has(createSelectionKey(String(widgetId), String(size))),
    [selectedStyleKeys],
  );

  const toggleStyleSelection = useCallback((widgetId, size) => {
    const key = createSelectionKey(String(widgetId), String(size));

    setSelectedStyleKeys((previousSelection) => {
      const nextSelection = new Set(previousSelection);
      if (nextSelection.has(key)) {
        nextSelection.delete(key);
      } else {
        nextSelection.add(key);
      }
      return nextSelection;
    });
  }, []);

  const getSelectedCountForWidget = useCallback(
    (widgetId) => {
      const selectionPrefix = `${String(widgetId)}:`;
      let count = 0;

      selectedStyleKeys.forEach((key) => {
        if (key.startsWith(selectionPrefix)) {
          count += 1;
        }
      });

      return count;
    },
    [selectedStyleKeys],
  );

  const pruneSelectionsByWidgetIds = useCallback((visibleWidgetIds) => {
    setSelectedStyleKeys((previousSelection) => {
      let hasChanges = false;
      const nextSelection = new Set();

      previousSelection.forEach((key) => {
        const { widgetId } = parseSelectionKey(key);
        if (widgetId && visibleWidgetIds.has(widgetId)) {
          nextSelection.add(key);
        } else {
          hasChanges = true;
        }
      });

      return hasChanges ? nextSelection : previousSelection;
    });
  }, []);

  const resetSelections = useCallback(() => {
    setSelectedStyleKeys(new Set());
  }, []);

  const buildSelectedWidgetConfigs = useCallback(
    (widgetLookup) => {
      const selectedWidgets = [];

      selectedStyleKeys.forEach((key) => {
        const { widgetId, size } = parseSelectionKey(key);
        if (!widgetId || !size) {
          return;
        }

        const layoutItem = createWidgetLayoutItem(widgetId, size, widgetLookup);
        if (layoutItem) {
          selectedWidgets.push(layoutItem);
        }
      });

      return selectedWidgets;
    },
    [selectedStyleKeys],
  );

  return {
    expandedWidgetId,
    setExpandedWidgetId,
    selectedStyleKeys,
    selectedCount: selectedStyleKeys.size,
    isStyleSelected,
    toggleStyleSelection,
    getSelectedCountForWidget,
    pruneSelectionsByWidgetIds,
    resetSelections,
    buildSelectedWidgetConfigs,
  };
};

export default useWidgetSelection;
